// src/components/AIAssistant.jsx — v17 FIXED: Draggable, Backend API, Mic Fixed
// S-052 (LOCKED — 2026-05-10): Tier-aware AI quota + voice gating.
// Adds usage counter, PaywallModal trigger on 429, voice gated by ai_advanced
// (3m+), Quick Prompts limited per tier (free=2, 1d=3, 1m=4, 3m=5, 12m=6).
import { useState, useRef, useEffect, useCallback } from 'react';
import { useApp } from '../context/AppContext';
import { useFeature } from '../hooks/useFeature';
import { PaywallModal } from './PremiumLock';
import { G, GD } from '../constants';

const BASE_URL = (window.location.hostname === 'localhost' ? (process.env.REACT_APP_API_URL || 'http://localhost:5000/api') : 'https://api.papercrypto.app/api');
const getToken = () => localStorage.getItem('pc_token');

// ── AI API — routed through backend to avoid CORS ─────────
// S-052 (LOCKED — 2026-05-10): Refactored to return structured response so
// caller can react to quota state. Returns object:
//   { reply: string, usage: { plan, used, limit, remaining } | null,
//     error: 'ai_quota_exceeded' | null }
// On 429 quota: error='ai_quota_exceeded' so AIAssistant can show PaywallModal.
// On other errors: returns reply with friendly fallback string (existing UX).
async function askAI(messages, retries = 2) {
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const res = await fetch(`${BASE_URL}/ai/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`,
        },
        body: JSON.stringify({ messages }),
        signal: AbortSignal.timeout(30000),
      });
      // S-052: 429 = quota exceeded. Don't retry (retrying won't help —
      // user needs to upgrade or wait for UTC midnight reset).
      if (res.status === 429) {
        const data = await res.json().catch(() => ({}));
        return {
          reply: data.message || 'Daily AI quota reached — upgrade for more messages.',
          usage: data.usage || null,
          error: 'ai_quota_exceeded',
        };
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (data.reply) {
        return { reply: data.reply, usage: data.usage || null, error: null };
      }
      throw new Error('Empty response');
    } catch (e) {
      if (attempt === retries) {
        return {
          reply: `Connection issue (attempt ${retries + 1}/${retries + 1}). Please check your internet and try again.`,
          usage: null,
          error: 'connection',
        };
      }
      await new Promise(r => setTimeout(r, 1000 * (attempt + 1)));
    }
  }
}

// ── Voice synthesis ────────────────────────────────────────
function loadVoices() {
  return new Promise(resolve => {
    const v = window.speechSynthesis?.getVoices() || [];
    if (v.length) { resolve(v); return; }
    const h = () => resolve(window.speechSynthesis?.getVoices() || []);
    if (window.speechSynthesis) {
      window.speechSynthesis.onvoiceschanged = h;
      setTimeout(() => resolve(window.speechSynthesis?.getVoices() || []), 1500);
    } else resolve([]);
  });
}

async function speak(text, voiceGender, onStart, onEnd) {
  if (!window.speechSynthesis) { onEnd?.(); return; }
  window.speechSynthesis.cancel();
  const voices = await loadVoices();
  const u = new SpeechSynthesisUtterance(text);
  if (voiceGender === 'momo') {
    const f = voices.find(v => ['samantha','victoria','karen','moira','zira','fiona','female'].some(n => v.name.toLowerCase().includes(n))) || voices[0];
    if (f) u.voice = f; u.pitch = 1.28; u.rate = 0.97;
  } else {
    const m = voices.find(v => ['daniel','alex','tom','fred','lee','male','david','mark'].some(n => v.name.toLowerCase().includes(n))) || voices[0];
    if (m) u.voice = m; u.pitch = 0.80; u.rate = 1.03;
  }
  u.volume = 1;
  u.onstart = () => onStart?.();
  u.onend = () => onEnd?.();
  u.onerror = () => onEnd?.();
  window.speechSynthesis.speak(u);
}

// ── Quick Prompts ─────────────────────────────────────────
const QUICK_PROMPTS = [
  { label:'📊 Market Overview', text:'Give me a quick crypto market overview and top opportunities.' },
  { label:'🎯 Entry Strategy',  text:'Explain the best entry strategies for volatile crypto markets.' },
  { label:'🛡️ Risk Management', text:'What are the most important risk management rules for crypto trading?' },
  { label:'🤖 Algo Strategy',   text:'Explain a simple moving average crossover algo strategy and how to backtest it.' },
  { label:'📈 Options Greeks',  text:'Explain Delta, Gamma, Theta and Vega in simple terms with examples.' },
  { label:'💰 Lot Sizing',      text:'How do I calculate the correct lot size for a crypto futures trade?' },
];

const PUNCH_WAKE = ['hey punch','hi punch','ok punch','okay punch','punch'];
const MOMO_WAKE  = ['hey momo','hi momo','ok momo','okay momo','momo'];

// ── Load/save draggable position ──────────────────────────
function loadPos() {
  try {
    const s = localStorage.getItem('pc_ai_pos');
    if (s) return JSON.parse(s);
  } catch {}
  return { right: 0, bottom: '28%' };
}
function savePos(p) {
  try { localStorage.setItem('pc_ai_pos', JSON.stringify(p)); } catch {}
}

// ══════════════════════════════════════════════════════════
export default function AIAssistant() {
  const { aiVoice, setAiVoice, aiEnabled, bg2, bg3, bord, tx2, darkMode } = useApp();
  // S-052: Tier-aware destructures. can('ai_advanced') gates voice synthesis;
  // PLAN_LABELS used in PaywallModal context. planId drives Quick Prompts limit.
  const { can, planId } = useFeature();

  const [open,     setOpen]     = useState(false);
  const [messages, setMessages] = useState([]);
  const [input,    setInput]    = useState('');
  const [loading,  setLoading]  = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [listening,setListening]= useState(false);
  const [wakeActive,setWakeActive]=useState(false);
  const [wakeStatus,setWakeStatus]=useState('');
  const [btnPos,   setBtnPos]   = useState(loadPos);
  const [dragging, setDragging] = useState(false);
  // S-052: AI usage state (driven by /chat response). null until first call.
  const [aiUsage,    setAiUsage]    = useState(null);
  // S-052: PaywallModal trigger context. null = closed; string = open with context.
  const [paywallCtx, setPaywallCtx] = useState(null);

  const chatRef      = useRef(null);
  const inputRef     = useRef(null);
  const manualRecog  = useRef(null);
  const wakeRecog    = useRef(null);
  const openRef      = useRef(false);
  const messagesRef  = useRef([]);
  const loadingRef   = useRef(false);
  const wakeActiveRef= useRef(false);
  const listeningRef = useRef(false);
  const dragStartRef = useRef(null);

  useEffect(() => { openRef.current    = open;        }, [open]);
  useEffect(() => { messagesRef.current= messages;    }, [messages]);
  useEffect(() => { loadingRef.current = loading;     }, [loading]);
  useEffect(() => { wakeActiveRef.current=wakeActive; }, [wakeActive]);
  useEffect(() => { listeningRef.current=listening;   }, [listening]);

  const isPunch   = aiVoice !== 'momo';
  const BOT_NAME  = isPunch ? 'PUNCH' : 'MOMO';
  const BOT_EMOJI = isPunch ? '🤖' : '🌸';
  const BOT_COLOR = isPunch ? '#6B8DE3' : '#B26BFF';
  const BOT_LABEL = isPunch ? '♂ Punch — Trading Pro' : '♀ Momo — Trading Queen';
  const MY_WAKES  = isPunch ? PUNCH_WAKE : MOMO_WAKE;

  useEffect(() => {
    if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight;
  }, [messages, loading]);

  useEffect(() => {
    if (open && messages.length === 0) {
      const g = isPunch
        ? `Yo! I'm Punch — your AI trading expert. Ask me anything about crypto, options, algo strategies! What's the play?`
        : `Hey! I'm Momo! 🌸 Your AI trading companion. Ask me about options, strategies, market analysis — anything!`;
      setMessages([{ role:'assistant', text:g }]);
      setTimeout(() => speak(g, aiVoice, () => setSpeaking(true), () => setSpeaking(false)), 400);
    }
  // eslint-disable-next-line
  }, [open]);

  const stopSpeaking = useCallback(() => {
    window.speechSynthesis?.cancel();
    setSpeaking(false);
  }, []);

  // ── Send message ─────────────────────────────────────────
  const sendMessage = useCallback(async (text) => {
    const userText = (text || input).trim();
    if (!userText || loadingRef.current) return;
    stopSpeaking();
    setInput('');
    const newMessages = [...messagesRef.current, { role:'user', text:userText }];
    setMessages(newMessages);
    setLoading(true);

    try {
      // S-052: askAI now returns { reply, usage, error } structured object.
      const r = await askAI(newMessages);
      const reply = r.reply;
      setMessages(prev => [...prev, { role:'assistant', text:reply }]);
      // Update usage counter (null when 12m unlimited or fallback error)
      if (r.usage) setAiUsage(r.usage);
      // Quota exceeded → surface desire-first PaywallModal (Rule K research:
      // industry-standard upgrade nudge AT the moment of friction).
      if (r.error === 'ai_quota_exceeded') {
        setPaywallCtx('default');
      }
      // S-052: Voice synthesis is gated to ai_advanced (3m+ per locked plan).
      // Free/1d/1m users get text-only replies; tier users get voice. UI also
      // gates the mic input button so wake-word + manual mic are tier-locked.
      if (can('ai_advanced')) {
        speak(reply, aiVoice, () => setSpeaking(true), () => setSpeaking(false));
      }
    } catch {
      setMessages(prev => [...prev, { role:'assistant', text:'Connection error. Please try again.' }]);
    }
    setLoading(false);
  // eslint-disable-next-line
  }, [input, stopSpeaking, aiVoice, can]);

  // ── Wake word ─────────────────────────────────────────────
  const stopWake = useCallback(() => {
    try { wakeRecog.current?.stop(); } catch {}
    wakeRecog.current = null;
  }, []);

  const startWake = useCallback(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR || listeningRef.current || wakeRecog.current) return;
    const r = new SR(); r.lang='en-US'; r.continuous=true; r.interimResults=true;
    r.onresult = (e) => {
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const t = (e.results[i][0]?.transcript || '').toLowerCase().trim();
        if (MY_WAKES.some(w => t.includes(w))) {
          r.stop(); wakeRecog.current = null; stopSpeaking();
          if (!openRef.current) setOpen(true);
          setWakeStatus(`👂 "${BOT_NAME}" heard! Ask your question...`);
          speak(isPunch ? "Yeah? What's up?" : "Yes! I'm here!", aiVoice, () => setSpeaking(true), () => { setSpeaking(false); });
          return;
        }
      }
    };
    r.onend = () => { wakeRecog.current = null; if (wakeActiveRef.current && !listeningRef.current) setTimeout(() => startWake(), 600); };
    r.onerror = (e) => { wakeRecog.current = null; if (e.error !== 'aborted' && wakeActiveRef.current) setTimeout(() => startWake(), 1200); };
    r.start(); wakeRecog.current = r;
  }, [MY_WAKES, BOT_NAME, isPunch, aiVoice, stopSpeaking]);

  // ── Manual mic ────────────────────────────────────────────
  const startListening = useCallback(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      alert('Voice input is not supported in this browser. Please use Chrome or Edge.');
      return;
    }
    stopSpeaking(); stopWake();
    try {
      const r = new SR(); r.lang='en-US'; r.continuous=false; r.interimResults=false;
      r.onresult = e => {
        const t = e.results[0]?.[0]?.transcript || '';
        setListening(false); listeningRef.current = false;
        if (t) sendMessage(t);
      };
      r.onend   = () => { setListening(false); listeningRef.current = false; if (wakeActiveRef.current) startWake(); };
      r.onerror = (e) => {
        setListening(false); listeningRef.current = false;
        if (e.error === 'not-allowed') alert('Microphone permission denied. Please allow microphone access in your browser settings.');
        else if (e.error === 'no-speech') { /* silent - user didn't speak */ }
        if (wakeActiveRef.current) startWake();
      };
      r.start();
      manualRecog.current = r;
      setListening(true); listeningRef.current = true;
    } catch (e) {
      alert('Could not start microphone: ' + e.message);
    }
  }, [sendMessage, stopSpeaking, stopWake, startWake]);

  const stopListening = useCallback(() => {
    try { manualRecog.current?.stop(); } catch {}
    setListening(false); listeningRef.current = false;
  }, []);

  useEffect(() => {
    if (wakeActive && !listeningRef.current) startWake();
    else if (!wakeActive) stopWake();
  // eslint-disable-next-line
  }, [wakeActive]);

  useEffect(() => () => { stopWake(); try { manualRecog.current?.stop(); } catch {} window.speechSynthesis?.cancel(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Draggable button logic ────────────────────────────────
  const handleDragStart = (e) => {
    e.preventDefault();
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    dragStartRef.current = { y: clientY, startBottom: btnPos.bottom === 0 ? 0 : parseFloat(btnPos.bottom) || 0 };
    setDragging(true);
  };

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e) => {
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      const dy = dragStartRef.current.y - clientY;
      const newBottom = Math.max(60, Math.min(window.innerHeight - 80, dragStartRef.current.startBottom + dy));
      setBtnPos({ right: 0, bottom: newBottom });
    };
    const onUp = () => {
      setDragging(false);
      savePos(btnPos);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
    };
  }, [dragging, btnPos]);

  if (!aiEnabled) return null;

  return (
    <>
      <style>{`
        @keyframes aiPulse { 0%,100%{box-shadow:0 0 12px ${BOT_COLOR}66}50%{box-shadow:0 0 28px ${BOT_COLOR}CC,0 0 48px ${BOT_COLOR}44} }
        @keyframes aiWakePulse { 0%,100%{box-shadow:0 0 8px #0ECB8166;transform:scale(1)}50%{box-shadow:0 0 22px #0ECB81CC;transform:scale(1.08)} }
        @keyframes aiSlideIn { from{opacity:0;transform:translateY(60px)}to{opacity:1;transform:translateY(0)} }
        @keyframes aiTyping { 0%,100%{opacity:0.3;transform:translateY(0)}50%{opacity:1;transform:translateY(-3px)} }
        @keyframes aiSpeakBar { 0%,100%{transform:scaleY(0.3)}50%{transform:scaleY(1)} }
      `}</style>

      {/* ── Floating draggable button ── */}
      {!open && (
        <div
          style={{
            position:'fixed', right: btnPos.right, bottom: btnPos.bottom,
            zIndex:500, cursor: dragging ? 'grabbing' : 'grab',
            touchAction:'none',
          }}
          onMouseDown={handleDragStart}
          onTouchStart={handleDragStart}
        >
          <div
            onClick={(e) => { if (!dragging) { e.stopPropagation(); setOpen(true); } }}
            style={{
              width:56, height:56, borderRadius:'50% 0 0 50%',
              background:`linear-gradient(135deg,${BOT_COLOR},${GD})`,
              display:'flex', alignItems:'center', justifyContent:'center',
              animation: wakeActive ? 'aiWakePulse 1.5s ease-in-out infinite' : 'aiPulse 2.4s ease-in-out infinite',
              userSelect:'none',
            }}>
            <span style={{ fontSize:22, userSelect:'none' }}>{BOT_EMOJI}</span>
          </div>
          <div style={{
            position:'absolute', right:60, top:'50%', transform:'translateY(-50%)',
            background:BOT_COLOR, color:'white', fontSize:9, fontWeight:800,
            padding:'3px 8px', borderRadius:'8px 0 0 8px', whiteSpace:'nowrap',
            letterSpacing:1, opacity:0.9, pointerEvents:'none',
          }}>{BOT_NAME}</div>
          {wakeStatus && (
            <div style={{
              position:'absolute', right:68, top:-32, background:'#0ECB81',
              color:'#0D0A00', fontSize:10, fontWeight:800, padding:'4px 10px',
              borderRadius:8, whiteSpace:'nowrap', pointerEvents:'none',
            }}>{wakeStatus}</div>
          )}
        </div>
      )}

      {/* ── Chat Panel ── */}
      {open && (
        <div style={{
          position:'fixed', right:0, bottom:56,
          width:'100%', maxWidth:420, height:'78vh',
          background:bg2, border:`1px solid ${bord}`,
          borderBottom:'none', borderRadius:'16px 16px 0 0',
          zIndex:800, display:'flex', flexDirection:'column',
          animation:'aiSlideIn 0.3s ease',
          boxShadow:'0 -8px 40px rgba(0,0,0,0.6)',
        }}>
          {/* Header */}
          <div style={{
            background:'linear-gradient(135deg,#1A0E00,#2D1E00)',
            borderRadius:'16px 16px 0 0', padding:'12px 16px',
            display:'flex', alignItems:'center', gap:10,
            borderBottom:`1px solid ${bord}`, flexShrink:0,
          }}>
            <div style={{
              width:40, height:40, borderRadius:'50%', flexShrink:0,
              background:`linear-gradient(135deg,${BOT_COLOR},${GD})`,
              display:'flex', alignItems:'center', justifyContent:'center',
              fontSize:20, boxShadow:`0 0 14px ${BOT_COLOR}77`,
            }}>{BOT_EMOJI}</div>
            <div style={{ flex:1 }}>
              <div style={{ fontWeight:800, color:G, fontSize:13 }}>{BOT_NAME}</div>
              <div style={{ color:tx2, fontSize:10 }}>{BOT_LABEL}</div>
              {wakeStatus && <div style={{ color:'#0ECB81', fontSize:10, fontWeight:700 }}>{wakeStatus}</div>}
              {/* S-052: Daily AI quota counter (hidden for 12m unlimited).
                  Tap → opens PaywallModal so user can upgrade for more messages. */}
              {aiUsage && aiUsage.limit !== -1 && (
                <div onClick={() => setPaywallCtx('default')}
                  style={{ color: aiUsage.remaining <= 1 ? '#F6465D' : tx2, fontSize:10, fontWeight:700,
                    cursor:'pointer', marginTop:2 }}>
                  💬 {aiUsage.used}/{aiUsage.limit} today {aiUsage.remaining <= 1 ? '· upgrade' : ''}
                </div>
              )}
            </div>
            {speaking && (
              <div style={{ display:'flex', gap:2, alignItems:'flex-end', height:20, marginRight:4 }}>
                {[6,10,14,10,6,12,8].map((h,i) => (
                  <div key={i} style={{ width:3, background:BOT_COLOR, borderRadius:2, height:h,
                    animation:`aiSpeakBar ${0.35+i*0.07}s ease-in-out infinite`, animationDelay:`${i*0.08}s` }}/>
                ))}
              </div>
            )}
            <div onClick={() => { const next=aiVoice==='punch'?'momo':'punch'; setAiVoice(next); setMessages([]); }}
              style={{ padding:'4px 10px', borderRadius:20, cursor:'pointer', background:`${BOT_COLOR}44`,
                border:`1px solid ${BOT_COLOR}`, fontSize:11, fontWeight:800, color:BOT_COLOR, whiteSpace:'nowrap' }}>
              {aiVoice==='punch'?'🤖→🌸':'🌸→🤖'}
            </div>
            {/* S-052: Wake-word listener gated by ai_advanced (3m+ per locked plan).
                Free/1d/1m users see locked 🔒 icon; click opens PaywallModal. */}
            <div onClick={() => {
                if (!can('ai_advanced')) { setPaywallCtx('default'); return; }
                if(wakeActive){stopWake();setWakeActive(false);wakeActiveRef.current=false;}
                else{setWakeActive(true);wakeActiveRef.current=true;}
              }}
              style={{ width:32, height:32, borderRadius:'50%',
                background: !can('ai_advanced') ? bg3 : (wakeActive?'#0ECB81':bg3),
                border:`1px solid ${!can('ai_advanced') ? bord : (wakeActive?'#0ECB81':bord)}`,
                display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', fontSize:16,
                opacity: can('ai_advanced') ? 1 : 0.55 }}>
              {can('ai_advanced') ? '👂' : '🔒'}
            </div>
            {speaking && <div onClick={stopSpeaking} style={{ cursor:'pointer', color:'#F6465D', fontSize:18 }}>⏹</div>}
            <div onClick={() => { setOpen(false); stopSpeaking(); }}
              style={{ cursor:'pointer', color:tx2, fontSize:22, lineHeight:1, padding:'0 4px' }}>✕</div>
          </div>

          {/* Messages */}
          <div ref={chatRef} style={{ flex:1, overflowY:'auto', padding:'12px 14px', display:'flex', flexDirection:'column', gap:10 }}>
            {messages.map((msg, i) => {
              const isBot = msg.role === 'assistant';
              return (
                <div key={i} style={{ display:'flex', flexDirection:isBot?'row':'row-reverse', gap:8, alignItems:'flex-end' }}>
                  {isBot && (
                    <div style={{ width:28, height:28, borderRadius:'50%', flexShrink:0,
                      background:`linear-gradient(135deg,${BOT_COLOR},${GD})`,
                      display:'flex', alignItems:'center', justifyContent:'center', fontSize:14 }}>{BOT_EMOJI}</div>
                  )}
                  <div onClick={() => isBot && speak(msg.text, aiVoice, ()=>setSpeaking(true), ()=>setSpeaking(false))}
                    style={{
                      maxWidth:'76%', padding:'10px 13px',
                      borderRadius:isBot?'14px 14px 14px 2px':'14px 14px 2px 14px',
                      background:isBot?(darkMode?'#1A1A2E':'#EEEEFF'):`linear-gradient(135deg,${GD},${G})`,
                      color:isBot?(darkMode?'#E0E0E0':'#1A1200'):'#0D0A00',
                      fontSize:13, lineHeight:1.65, fontWeight:isBot?400:600,
                      border:isBot?`1px solid ${BOT_COLOR}33`:'none', cursor:isBot?'pointer':'default',
                    }}>
                    {msg.text}
                    {isBot && <div style={{ color:tx2, fontSize:9, marginTop:4 }}>🔊 Tap to replay</div>}
                  </div>
                </div>
              );
            })}
            {loading && (
              <div style={{ display:'flex', gap:8, alignItems:'flex-end' }}>
                <div style={{ width:28, height:28, borderRadius:'50%', background:`linear-gradient(135deg,${BOT_COLOR},${GD})`,
                  display:'flex', alignItems:'center', justifyContent:'center', fontSize:14 }}>{BOT_EMOJI}</div>
                <div style={{ padding:'10px 14px', borderRadius:'14px 14px 14px 2px',
                  background:darkMode?'#1A1A2E':'#EEEEFF', border:`1px solid ${BOT_COLOR}33`, display:'flex', gap:4 }}>
                  {[0,1,2].map(i => (
                    <div key={i} style={{ width:7, height:7, borderRadius:'50%', background:BOT_COLOR,
                      animation:'aiTyping 1s ease-in-out infinite', animationDelay:`${i*0.22}s` }}/>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Quick prompts — S-052 tier-aware capability gating.
              Per locked plan: free=2, 1d=3, 1m=4, 3m=5, 12m=6 visible/clickable.
              Beyond-tier prompts render with lock icon + click triggers PaywallModal. */}
          <div style={{ padding:'6px 12px', borderTop:`1px solid ${bord}`, display:'flex', gap:6, overflowX:'auto', flexShrink:0 }}>
            {QUICK_PROMPTS.map((p,i) => {
              const tierLimits = { free:2, '1d':3, '1m':4, '3m':5, '12m':6 };
              const allowed = i < (tierLimits[planId] != null ? tierLimits[planId] : tierLimits.free);
              return (
                <div key={i}
                  onClick={() => allowed ? sendMessage(p.text) : setPaywallCtx('default')}
                  style={{ padding:'5px 12px', borderRadius:20, background:bg3,
                    border:`1px solid ${bord}`, fontSize:11, fontWeight:600,
                    whiteSpace:'nowrap', cursor:'pointer', flexShrink:0,
                    color: allowed ? BOT_COLOR : tx2,
                    opacity: allowed ? 1 : 0.55 }}>
                  {allowed ? p.label : `🔒 ${p.label}`}
                </div>
              );
            })}
          </div>

          {/* Input row */}
          <div style={{ padding:'10px 12px', borderTop:`1px solid ${bord}`, display:'flex', gap:8, alignItems:'center', flexShrink:0 }}>
            <input ref={inputRef} value={input} onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key==='Enter' && !e.shiftKey && sendMessage()}
              placeholder={listening ? '🎤 Listening... speak now' : `Ask ${BOT_NAME} anything...`}
              style={{ flex:1, background:bg3, border:`1px solid ${listening?'#0ECB81':bord}`,
                color:darkMode?'#F0F0F0':'#1A1200', borderRadius:22, padding:'10px 16px',
                fontFamily:'inherit', fontSize:13, outline:'none' }} />
            {/* S-052: Voice input mic gated by ai_advanced (3m+).
                Free/1d/1m users see locked icon; click opens PaywallModal. */}
            <div onClick={() => {
                if (!can('ai_advanced')) { setPaywallCtx('default'); return; }
                listening ? stopListening() : startListening();
              }}
              style={{ width:40, height:40, borderRadius:'50%',
                background: !can('ai_advanced') ? bg3 : (listening?'linear-gradient(135deg,#0A6B3F,#0ECB81)':bg3),
                border:`1px solid ${!can('ai_advanced') ? bord : (listening?'#0ECB81':bord)}`,
                display:'flex', alignItems:'center', justifyContent:'center',
                cursor:'pointer', fontSize:18, flexShrink:0,
                opacity: can('ai_advanced') ? 1 : 0.55,
                animation: (listening && can('ai_advanced')) ? 'aiWakePulse 1s ease-in-out infinite' : 'none' }}>
              {can('ai_advanced') ? '🎤' : '🔒'}
            </div>
            <div onClick={() => sendMessage()}
              style={{ width:40, height:40, borderRadius:'50%',
                background:input.trim()?`linear-gradient(135deg,${GD},${G})`:bg3,
                border:`1px solid ${input.trim()?G:bord}`,
                display:'flex', alignItems:'center', justifyContent:'center',
                cursor:input.trim()?'pointer':'default', fontSize:18, flexShrink:0, opacity:loading?0.5:1 }}>▲</div>
          </div>
        </div>
      )}

      {open && (
        <div onClick={() => { setOpen(false); stopSpeaking(); }}
          style={{ position:'fixed', inset:0,
            // v270 (G4.11.1): 0.4 max for AdSense Better Ad Standards compliance — DO NOT increase above 0.5
            background:'rgba(0,0,0,0.4)', zIndex:799 }}/>
      )}

      {/* S-052: Centralized PaywallModal — opens when user hits any AI tier
          gate (quota exceeded, voice features locked, premium quick prompts).
          Reuses canonical PaywallModal from PremiumLock.jsx (Rule H — Single Source). */}
      {paywallCtx && (
        <PaywallModal
          feature="AI Trading Assistant"
          context={paywallCtx}
          onClose={() => setPaywallCtx(null)}
          setPage={() => {
            setPaywallCtx(null);
            if (typeof window !== 'undefined' && window.location) {
              window.location.hash = '#/premium';
            }
          }}
        />
      )}
    </>
  );
}
